import axiosInstance from '../axios/axiosInstance';
import type { PaginatedResponse } from './types';

// Billing Status Enum
export enum BillingStatus {
  QUOTED = 'QUOTED',
  INVOICED = 'INVOICED',
  PAID = 'PAID',
}

export interface Project {
  _id: string;
  title: string;
  status: string;
  workspaceId: string;
  workspaceName: string;
  organizationId: string;
  organizationName: string;
  respondentsMax: number;
  respondentsCount: number;
  conversationsTotal: number;
  conversationsCompleted: number;
  createdAt: string;
  deletedAt?: string;
  isDeleted?: boolean;
  // Billing Status (Super Admin Only)
  billingStatus?: BillingStatus;
  billingStatusUpdatedAt?: string;
  billingStatusUpdatedBy?: {
    _id: string;
    email: string;
    name?: string;
  };
  billingNotes?: string;
  billingAmount?: number;
  billingCurrency?: string;
  billingReference?: string;
}

export interface UpdateBillingStatusPayload {
  billingStatus: BillingStatus;
  notes?: string;
  billingAmount?: number;
  billingCurrency?: string;
  billingReference?: string;
}

export interface BillingHistoryEntry {
  _id: string;
  billingStatus: BillingStatus;
  notes?: string;
  billingAmount?: number;
  billingCurrency?: string;
  billingReference?: string;
  updatedAt: string;
  updatedBy?: {
    _id: string;
    email: string;
    name?: string;
  };
}

export interface BillingHistoryFilters {
  page?: number;
  limit?: number;
}

export interface ProjectFilters {
  page?: number;
  limit?: number;
  search?: string;
  includeDeleted?: boolean;
  sortBy?: 'title' | 'status' | 'billingStatus' | 'respondentsCount' | 'conversationsTotal' | 'createdAt' | 'organizationName' | 'workspaceName';
  sortOrder?: 'asc' | 'desc';
}

export const projectsApi = {
  /**
   * Get all projects with filters and pagination
   */
  getAll: async (params: ProjectFilters): Promise<PaginatedResponse<Project>> => {
    const { data } = await axiosInstance.get<PaginatedResponse<Project>>('/projects', { params });
    return data;
  },

  // ==================== Billing Status ====================

  /**
   * Update project billing status
   */
  updateBillingStatus: async (
    id: string,
    payload: UpdateBillingStatusPayload
  ): Promise<Project> => {
    const { data } = await axiosInstance.patch<Project>(
      `/projects/${id}/billing`,
      payload
    );
    return data;
  },

  /**
   * Get project with billing details
   */
  getWithBilling: async (id: string): Promise<Project> => {
    const { data } = await axiosInstance.get<Project>(`/projects/${id}/billing`);
    return data;
  },

  /**
   * Get project billing history
   */
  getBillingHistory: async (
    id: string,
    params?: BillingHistoryFilters
  ): Promise<{ data: BillingHistoryEntry[]; total: number; page: number; totalPages: number; limit: number }> => {
    const { data } = await axiosInstance.get(`/projects/${id}/billing/history`, { params });
    return data;
  },

  /**
   * Restore a soft-deleted project
   */
  restore: async (id: string, organizationId: string, workspaceId: string): Promise<Project> => {
    const { data } = await axiosInstance.patch<Project>(
      `/organizations/${organizationId}/workspaces/${workspaceId}/projects/${id}/restore`
    );
    return data;
  },
};
